import { useState, useEffect } from 'react';
import {
  View,
  Pressable,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import DropDownPicker from 'react-native-dropdown-picker';

import CustomText from '../components/CustomText';
import TodoList from '../components/TodoList';
import AttachmentList from '../components/AttachmentList';
import StatusBadge from '../components/StatusBadge';
import ConfirmationModal from '../components/ConfirmationModal';
import ActivityTimeline from '../components/ActivityTimeline';
import { ReportDetailsSkeleton } from '../components/LoadingSkeleton';
import styles from '../style/screens/adminReportDetailsStyles';
import Util from '../util/util.js';
import { getIsOrgAdmin } from '../util/storage';
import { formatDate, formatRelativeTime } from '../util/format';
import ChipSection from '../components/ChipSection.js';
import MonetaImageGallery from '../components/MonetaImageGallery';
import WorkOrderLinkButton from '../components/WorkOrderLinkButton';
import { webTitleRef } from '../components/WebTooltip';
import { Chip_Styles, Web_Chip_Styles } from '../style/ChipStyles.js';
import { ReportHeader, ReportContent, ReportSummaryRow, ReportComments, ReportLayout } from '../components/ReportView';
import { useResponsiveStyles, useValidChips, CHIP_TYPES, useAdminReportData, markReportViewed } from '../hooks';
import { hroLabels } from '../util/constants';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const AdminReportDetails = ({ route, navigation }) => {
  const { t } = useTranslation('reports');

  // Navigation params
  const { uuid, defaultOrganization, userAdmin, noApp } = route.params;

  // Use responsive styles hook for platform-aware styling
  const { isWeb, isDesktop, webStyles, mobileStyles } = useResponsiveStyles();

  // Use admin report data hook for all report state and actions
  const {
    loading,
    organization,
    content,
    timestamp,
    reporter,
    isUrgent,
    isClosed,
    imageURLs,
    setImageURLs,
    comments,
    kpis,
    setKpis,
    hros,
    setHros,
    locations,
    setLocations,
    aiKpiOverridden,
    aiHroOverridden,
    aiKpis,
    aiHros,
    isEditing,
    setIsEditing,
    triggerKpisUpdate,
    triggerHrosUpdate,
    triggerLocationsUpdate,
    overrideAI,
    restoreAI,
    addComment,
    editComment,
    deleteComment,
    // Ticketing fields
    status,
    statusUpdating,
    statusHistory,
    assignedTo,
    escalationLevel,
    nextEscalationAt,
    escalationWindowMs,
    nextReminderAt,
    reminderWindowMs,
    remindersMuted,
    remindersCapped,
    todos,
    attachments,
    analysts,
    analystsLoading,
    activityLog,
    lastUpdated,
    // Ticketing actions
    updateStatus,
    updateAssignment,
    escalateTicket,
    toggleRemindersMuted,
    addTodo,
    toggleTodo,
    deleteTodo,
    uploadAttachment,
    downloadAttachment,
    deleteAttachment,
  } = useAdminReportData(uuid, { defaultOrganization });

  // Local UI state
  const [commentInputText, setCommentInputText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [adminOnlyComment, setAdminOnlyComment] = useState(false);
  const [statusHistoryExpanded, setStatusHistoryExpanded] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);

  // Load current user email + org-admin status on mount
  useEffect(() => {
    Util.getUserEmail().then(setCurrentUserEmail);
    getIsOrgAdmin().then((v) => setIsOrgAdmin(v === true));
  }, []);

  // Record that this user viewed the report (powers unread filtering)
  useEffect(() => {
    markReportViewed(uuid);
  }, [uuid]);

  // Dropdown state
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [assignmentDropdownOpen, setAssignmentDropdownOpen] = useState(false);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    title: '',
    message: '',
    confirmText: '',
    confirmColor: AppStyles.colorSet.error,
    icon: 'alert-triangle',
    onConfirm: () => {},
  });

  // Fetch valid chip values for this organization
  const { chipValues: validKPIs } = useValidChips(organization, CHIP_TYPES.KPI);
  const { chipValues: validLocations } = useValidChips(organization, CHIP_TYPES.LOCATION);
  // Recomputed each render so the chip options follow a language change.
  const validHROs = hroLabels();

  // Check authentication when opened from email link (noApp=true)
  useEffect(() => {
    const checkAuth = async () => {
      if (!noApp) return;

      const isValid = await Util.isTokenValid();
      if (!isValid) {
        navigation.navigate('Login', {
          skipAutoLogin: false,
          redirectTo: 'AdminReportDetails',
          redirectParams: { uuid, defaultOrganization, userAdmin, noApp },
        });
      }
    };
    checkAuth();
  }, [noApp, navigation, uuid, defaultOrganization, userAdmin]);

  // Keyboard shortcuts (web only)
  useEffect(() => {
    if (!isWeb) return;

    const handleKeyDown = (e) => {
      // Cmd/Ctrl + E: Toggle edit mode
      if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
        e.preventDefault();
        setIsEditing((prev) => !prev);
      }

      // Cmd/Ctrl + Enter: Submit comment (only if comment input has focus)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        const activeElement = document.activeElement;
        if (activeElement && activeElement.placeholder === t('admin.addComment')) {
          e.preventDefault();
          postCommentToEntry();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commentInputText, adminOnlyComment]); // Dependencies for postCommentToEntry

  // Format countdown (milliseconds remaining → "3h", "2d")
  const formatCountdown = (ms) => {
    if (ms <= 0) return '<1h';
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (hours < 1) return '<1h';
    if (hours < 24) return `${hours}h`;
    return `${days}d ${hours % 24}h`;
  };

  // Post comment handler
  const postCommentToEntry = async () => {
    if (!commentInputText.trim() || submittingComment) return;

    // Capture current values and reset immediately to avoid stale closure issues
    const textToSubmit = commentInputText;
    const isAdminOnly = adminOnlyComment;
    setCommentInputText('');
    setAdminOnlyComment(false);
    setSubmittingComment(true);

    try {
      await addComment(textToSubmit, isAdminOnly);
    } catch (error) {
      Util.printAPIError(error);
      // Restore values on error
      setCommentInputText(textToSubmit);
      setAdminOnlyComment(isAdminOnly);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Get chip styles based on platform
  const chipStyles = isWeb ? Web_Chip_Styles : Chip_Styles;

  // Check if we have AI classifications to show
  // Show AI section even when overridden (so user can restore)
  const hasAiKpis = aiKpis && aiKpis.length > 0;
  // Show AI HRO if: there's an AI HRO AND (no manual HRO selected OR HRO is overridden so user can restore)
  const hasAiHros = aiHros && aiHros.length > 0;
  const showAiHros = hasAiHros && (!(hros && hros.length > 0) || aiHroOverridden);

  // Check if we have tags to show in each subsection
  const hasKpis = kpis && kpis.length > 0;
  const hasHros = hros && hros.length > 0;
  const hasLocations = locations && locations.length > 0;

  // Layout props
  const containerStyle = isWeb ? webStyles.container : mobileStyles.container;

  const headerProps = {
    organization,
    admin: true,
    userAdmin,
    type: 'pop',
    noApp,
  };

  const footerProps = {
    admin: true,
    organization: defaultOrganization,
    userAdmin,
    noApp,
  };

  return (
    <ReportLayout
      isWeb={isWeb}
      containerStyle={containerStyle}
      headerProps={headerProps}
      footerProps={footerProps}
    >
      {loading ? (
        <ReportDetailsSkeleton />
      ) : (
        <>
          <ReportHeader uuid={uuid} isUrgent={isUrgent} isClosed={isClosed} escalationLevel={escalationLevel} organization={organization} webStyles={webStyles} />

          {/* At-a-glance summary: read-only echo of status/tags; editing stays in the sections below */}
          <ReportSummaryRow
            status={status}
            kpis={kpis}
            locations={locations}
            assignedTo={assignedTo}
          />

          <ReportContent content={content} timestamp={timestamp} reporter={reporter} loading={loading} />

          <MonetaImageGallery images={imageURLs} setImages={!isWeb ? setImageURLs : undefined} />

          {/* Tags inline below report content */}
          <View style={styles.tagsInlineSection}>
            {/* AI Suggestions - compact single section with inline hide buttons */}
            {(hasAiKpis || showAiHros) && (
              <View style={styles.aiTagsInline}>
                <View style={styles.tagLabelRow}>
                  <Feather name="cpu" size={14} color={AppStyles.colorSet.brandPrimary} />
                  <CustomText font="medium" size={12} color={AppStyles.colorSet.textMuted}>
                    {t('admin.aiSuggestions')}
                  </CustomText>
                </View>
                <View style={styles.aiChipsRow}>
                  {/* AI Hazard Classification chips with inline hide */}
                  {hasAiKpis && (
                    <View style={styles.aiChipGroup}>
                      <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted} style={{ marginRight: 4 }}>
                        {t('admin.hazardColon')}
                      </CustomText>
                      {(aiKpis || []).map((chip, idx) => (
                        <View
                          key={`ai-kpi-${idx}`}
                          style={[
                            styles.aiChipWithHide,
                            aiKpiOverridden && styles.aiChipHidden,
                          ]}
                        >
                          <Feather name="zap" size={12} color={aiKpiOverridden ? AppStyles.colorSet.textMuted : '#B45309'} />
                          <CustomText
                            font="medium"
                            size={12}
                            color={aiKpiOverridden ? AppStyles.colorSet.textMuted : AppStyles.colorSet.textPrimary}
                            style={aiKpiOverridden && { textDecorationLine: 'line-through' }}
                          >
                            {chip}
                          </CustomText>
                          {isEditing && (
                            <Pressable
                              ref={webTitleRef(aiKpiOverridden ? t('admin.restoreAiKpi') : t('admin.hideAiKpi'))}
                              onPress={() => aiKpiOverridden ? restoreAI('kpi') : overrideAI('kpi')}
                              hitSlop={8}
                              style={styles.aiChipHideButton}
                            >
                              <Feather
                                name={aiKpiOverridden ? 'rotate-ccw' : 'x'}
                                size={12}
                                color={aiKpiOverridden ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textMuted}
                              />
                            </Pressable>
                          )}
                        </View>
                      ))}
                    </View>
                  )}
                  {/* AI HRO chip with inline hide */}
                  {showAiHros && (
                    <View style={styles.aiChipGroup}>
                      <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted} style={{ marginRight: 4 }}>
                        HRO:
                      </CustomText>
                      {(aiHros || []).map((chip, idx) => (
                        <View
                          key={`ai-hro-${idx}`}
                          style={[
                            styles.aiChipWithHide,
                            aiHroOverridden && styles.aiChipHidden,
                          ]}
                        >
                          <Feather name="zap" size={12} color={aiHroOverridden ? AppStyles.colorSet.textMuted : '#B45309'} />
                          <CustomText
                            font="medium"
                            size={12}
                            color={aiHroOverridden ? AppStyles.colorSet.textMuted : AppStyles.colorSet.textPrimary}
                            style={aiHroOverridden && { textDecorationLine: 'line-through' }}
                          >
                            {chip}
                          </CustomText>
                          {isEditing && (
                            <Pressable
                              ref={webTitleRef(aiHroOverridden ? t('admin.restoreAiHro') : t('admin.hideAiHro'))}
                              onPress={() => aiHroOverridden ? restoreAI('hro') : overrideAI('hro')}
                              hitSlop={8}
                              style={styles.aiChipHideButton}
                            >
                              <Feather
                                name={aiHroOverridden ? 'rotate-ccw' : 'x'}
                                size={12}
                                color={aiHroOverridden ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textMuted}
                              />
                            </Pressable>
                          )}
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              </View>
            )}

            {/* Editable Tags - inline with edit toggle */}
            <View style={styles.editableTagsInline}>
              <Pressable
                ref={webTitleRef(isEditing ? t('admin.finishEditingTags') : t('admin.editTags'))}
                onPress={() => setIsEditing(!isEditing)}
                style={({ hovered, focused }) => [
                  styles.tagLabelRow,
                  { cursor: 'pointer' },
                  (hovered || focused) && styles.tagLabelRowHovered,
                ]}
              >
                {({ hovered, focused }) => (
                  <>
                    <Feather
                      name="tag"
                      size={14}
                      color={isEditing || hovered || focused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textMuted}
                    />
                    <CustomText font="medium" size={12} color={AppStyles.colorSet.textMuted}>
                      {t('details.tags')}
                    </CustomText>
                    <Feather
                      name={isEditing ? 'check' : 'edit-2'}
                      size={14}
                      color={isEditing || hovered || focused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                      style={{ marginLeft: 4 }}
                    />
                    {isEditing && (
                      <CustomText font="medium" size={11} color="#B45309" style={{ marginLeft: 4 }}>
                        {t('admin.editing')}
                      </CustomText>
                    )}
                  </>
                )}
              </Pressable>

              {/* Tag categories with labels */}
              {(hasKpis || isEditing) && (
                <View style={styles.tagCategory}>
                  <CustomText font="medium" size={11} color={AppStyles.colorSet.textMuted} style={styles.tagCategoryLabel}>
                    {t('admin.hazard')}
                  </CustomText>
                  <View style={styles.chipRow}>
                    <ChipSection
                      styles={chipStyles}
                      deleteChips={triggerKpisUpdate}
                      addChip={triggerKpisUpdate}
                      doSynchronize={isEditing}
                      canAdd={isEditing}
                      canDelete={isEditing}
                      sortChips={true}
                      validValues={validKPIs}
                      Chips={kpis}
                      setChips={setKpis}
                    />
                  </View>
                </View>
              )}

              {(hasLocations || isEditing) && (
                <View style={styles.tagCategory}>
                  <CustomText font="medium" size={11} color={AppStyles.colorSet.textMuted} style={styles.tagCategoryLabel}>
                    {t('admin.location')}
                  </CustomText>
                  <View style={styles.chipRow}>
                    <ChipSection
                      styles={chipStyles}
                      deleteChips={triggerLocationsUpdate}
                      addChip={triggerLocationsUpdate}
                      doSynchronize={isEditing}
                      canAdd={isEditing}
                      canDelete={isEditing}
                      validValues={validLocations}
                      Chips={locations}
                      setChips={setLocations}
                    />
                  </View>
                </View>
              )}

              {/* Only show HRO selector when: has manual HRO OR (editing AND (no AI HRO OR AI HRO is hidden)) */}
              {(hasHros || (isEditing && (!hasAiHros || aiHroOverridden))) && (
                <View style={styles.tagCategory}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <CustomText font="medium" size={11} color={AppStyles.colorSet.error} style={styles.tagCategoryLabel}>
                      HRO
                    </CustomText>
                    {isEditing && !hasHros && aiHroOverridden && (
                      <CustomText font="regular" size={10} color={AppStyles.colorSet.textMuted}>
                        {t('admin.selectToOverride')}
                      </CustomText>
                    )}
                  </View>
                  <View style={styles.chipRow}>
                    <ChipSection
                      styles={chipStyles}
                      deleteChips={triggerHrosUpdate}
                      addChip={triggerHrosUpdate}
                      error
                      doSynchronize={isEditing}
                      canAdd={isEditing && hros.length === 0}
                      canDelete={isEditing}
                      validValues={validHROs}
                      Chips={hros}
                      setChips={setHros}
                    />
                  </View>
                </View>
              )}
            </View>
          </View>

          {/* Compact Status Bar */}
          <View style={[styles.statusBar, { zIndex: (statusDropdownOpen || assignmentDropdownOpen) ? 3000 : 1 }]}>
            {/* Admin only indicator */}
            <View style={styles.adminOnlyIndicator}>
              <Feather name="lock" size={10} color={AppStyles.colorSet.textMuted} />
              <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted}>
                {t('admin.adminOnly')}
              </CustomText>
            </View>
            <View style={[styles.statusBarRow, !isDesktop && styles.statusBarRowNarrow]}>
              {/* Status Dropdown */}
              <View style={[styles.statusBarItem, isDesktop ? styles.statusItemDesktop : styles.statusBarItemNarrow, { zIndex: 3000 }]}>
                <DropDownPicker
                  open={statusDropdownOpen}
                  value={status}
                  items={[
                    { label: t('status.Open'), value: 'Open' },
                    { label: t('status.InProgress'), value: 'InProgress' },
                    { label: t('status.Closed'), value: 'Closed' },
                  ]}
                  setOpen={(open) => {
                    setStatusDropdownOpen(open);
                    if (open) setAssignmentDropdownOpen(false);
                  }}
                  setValue={(callback) => {
                    const newValue = typeof callback === 'function' ? callback(status) : callback;
                    // Show confirmation for closing or reopening
                    if (newValue === 'Closed' && status !== 'Closed') {
                      setConfirmModal({
                        visible: true,
                        title: t('admin.closeReportTitle'),
                        message: t('admin.closeReportMessage'),
                        confirmText: t('admin.closeReport'),
                        confirmColor: AppStyles.colorSet.error,
                        icon: 'check-circle',
                        onConfirm: () => {
                          updateStatus(newValue);
                          setConfirmModal((prev) => ({ ...prev, visible: false }));
                        },
                      });
                    } else if (status === 'Closed' && newValue !== 'Closed') {
                      setConfirmModal({
                        visible: true,
                        title: t('admin.reopenReportTitle'),
                        message: t('admin.reopenReportMessage'),
                        confirmText: t('admin.reopenReport'),
                        confirmColor: AppStyles.colorSet.success,
                        icon: 'refresh-cw',
                        onConfirm: () => {
                          updateStatus(newValue);
                          setConfirmModal((prev) => ({ ...prev, visible: false }));
                        },
                      });
                    } else {
                      updateStatus(newValue);
                    }
                  }}
                  style={styles.compactDropdown}
                  dropDownContainerStyle={styles.compactDropdownContainer}
                  textStyle={styles.compactDropdownText}
                  zIndex={3000}
                  zIndexInverse={1000}
                />
              </View>

              {/* Assignment Dropdown */}
              <View style={[styles.statusBarItem, isDesktop ? styles.assignItemDesktop : styles.statusBarItemNarrow, { zIndex: 2000 }]}>
                <DropDownPicker
                  open={assignmentDropdownOpen}
                  value={assignedTo}
                  items={[
                    { label: t('admin.unassigned'), value: null },
                    ...analysts.map((a) => ({ label: a.email, value: a.email })),
                  ]}
                  setOpen={(open) => {
                    setAssignmentDropdownOpen(open);
                    if (open) setStatusDropdownOpen(false);
                  }}
                  setValue={(callback) => {
                    const newValue = typeof callback === 'function' ? callback(assignedTo) : callback;
                    updateAssignment(newValue);
                  }}
                  placeholder={t('admin.unassigned')}
                  disabled={analystsLoading || statusUpdating}
                  loading={analystsLoading || statusUpdating}
                  activityIndicatorColor={AppStyles.colorSet.brandPrimary}
                  activityIndicatorSize={16}
                  style={[
                    styles.compactDropdown,
                    (analystsLoading || statusUpdating) && styles.compactDropdownDisabled,
                  ]}
                  dropDownContainerStyle={styles.compactDropdownContainer}
                  textStyle={styles.compactDropdownText}
                  zIndex={2000}
                  zIndexInverse={2000}
                />
              </View>

              {/* Escalate Button */}
              {!isClosed && escalationLevel < 2 && (
                <Pressable
                  ref={webTitleRef(t('admin.escalateTooltip', { level: escalationLevel + 1 }))}
                  style={({ pressed, hovered, focused }) => [
                    styles.escalateButton,
                    !isDesktop && styles.hugStart,
                    { cursor: 'pointer' },
                    (hovered || focused) && styles.escalateButtonHovered,
                    pressed && { opacity: 0.7 },
                  ]}
                  onPress={escalateTicket}
                >
                  <Feather name="arrow-up-circle" size={14} color="#DC2626" />
                  <CustomText style={{ marginLeft: 4 }} font="medium" size={12} color="#DC2626">
                    {t('admin.escalate')}
                  </CustomText>
                </Pressable>
              )}

              {/* Mute / Unmute Reminders Button (OrgAdmins only) */}
              {!isClosed && isOrgAdmin && (
                <Pressable
                  ref={webTitleRef(remindersMuted
                    ? t('admin.resumeRemindersTooltip')
                    : t('admin.muteRemindersTooltip'))}
                  style={({ pressed, hovered, focused }) => [
                    styles.muteButton,
                    !isDesktop && styles.hugStart,
                    { cursor: 'pointer' },
                    (hovered || focused) && styles.muteButtonHovered,
                    pressed && { opacity: 0.7 },
                  ]}
                  onPress={toggleRemindersMuted}
                >
                  <Feather
                    name={remindersMuted ? 'bell' : 'bell-off'}
                    size={14}
                    color={AppStyles.colorSet.textSecondary}
                  />
                  <CustomText
                    style={{ marginLeft: 4 }}
                    font="medium"
                    size={12}
                    color={AppStyles.colorSet.textSecondary}
                  >
                    {remindersMuted ? t('admin.resumeReminders') : t('admin.muteReminders')}
                  </CustomText>
                </Pressable>
              )}

              {/* Reminders Muted indicator */}
              {!isClosed && remindersMuted && (
                <View style={[styles.statusBarMeta, { flexDirection: 'row', alignItems: 'center', gap: 4 }]}>
                  <Feather name="bell-off" size={12} color={AppStyles.colorSet.textMuted} />
                  <CustomText font="medium" size={11} color={AppStyles.colorSet.textMuted}>
                    {t('admin.remindersMuted')}
                  </CustomText>
                </View>
              )}

              {/* Max reminders reached indicator */}
              {!isClosed && !remindersMuted && remindersCapped && (
                <View style={[styles.statusBarMeta, { flexDirection: 'row', alignItems: 'center', gap: 4 }]}>
                  <Feather name="bell-off" size={12} color={AppStyles.colorSet.textMuted} />
                  <CustomText font="medium" size={11} color={AppStyles.colorSet.textMuted}>
                    {escalationLevel > 0 ? `L${escalationLevel} · ` : ''}
                    {t('list.maxRemindersReached')}
                  </CustomText>
                </View>
              )}

              {/* Reminder Info — pre-escalation only; post-escalation we show the escalation block */}
              {!isClosed && !remindersMuted && !remindersCapped && escalationLevel === 0 && nextReminderAt && reminderWindowMs > 0 && (() => {
                const remaining = nextReminderAt - Date.now();
                const isPast = remaining <= 0;
                const isOverdue = isPast && Math.abs(remaining) > reminderWindowMs;
                const pct = 1 - (remaining / reminderWindowMs);
                // Only surface when overdue or past 50% of the window
                if (!isPast && pct < 0.5) return null;
                const color = isOverdue ? '#DC2626' : pct >= 0.75 ? '#EA580C' : '#D97706';
                const label = isOverdue
                  ? t('admin.reminderOverdue')
                  : t('admin.reminderIn', { time: formatCountdown(remaining) });
                return (
                  <View style={[styles.statusBarMeta, { flexDirection: 'row', alignItems: 'center', gap: 4 }]}>
                    <Feather name="bell" size={12} color={color} />
                    <CustomText font="medium" size={11} color={color}>
                      {label}
                    </CustomText>
                  </View>
                );
              })()}

              {/* Escalation Info */}
              {!isClosed && !remindersMuted && !remindersCapped && nextEscalationAt && escalationWindowMs > 0 && (() => {
                const remaining = nextEscalationAt - Date.now();
                const isPast = remaining <= 0;
                const isOverdue = isPast && Math.abs(remaining) > escalationWindowMs;
                const pct = 1 - (remaining / escalationWindowMs);
                // Only show if already escalated, overdue, or past 50% of window
                if (escalationLevel === 0 && !isPast && pct < 0.5) return null;
                const color = isOverdue ? '#DC2626' : pct >= 0.75 ? '#EA580C' : '#D97706';
                const label = isOverdue
                  ? t('admin.escalationOverdue')
                  : t('admin.escalatesIn', { time: formatCountdown(remaining) });
                return (
                  <View style={[styles.statusBarMeta, { flexDirection: 'row', alignItems: 'center', gap: 4 }]}>
                    <Feather name="arrow-up-circle" size={12} color={color} />
                    <CustomText font="medium" size={11} color={color}>
                      {escalationLevel > 0 ? `L${escalationLevel} · ` : ''}{label}
                    </CustomText>
                  </View>
                );
              })()}

              {/* Last Updated */}
              {lastUpdated && (
                <View style={styles.statusBarMeta}>
                  <CustomText font="regular" size={11} color={AppStyles.colorSet.textMuted}>
                    {t('details.updatedAgo', {
                      time: formatRelativeTime(lastUpdated, { midSentence: true }),
                    })}
                  </CustomText>
                </View>
              )}
            </View>

            {/* Status History - collapsible */}
            {statusHistory && statusHistory.length > 1 && (
              <View style={styles.statusHistoryCompact}>
                <Pressable
                  onPress={() => setStatusHistoryExpanded(!statusHistoryExpanded)}
                  style={styles.statusHistoryToggle}
                >
                  <CustomText font="medium" size={11} color={AppStyles.colorSet.info}>
                    {t('details.historyCount', { count: statusHistory.length })}
                  </CustomText>
                  <Feather
                    name={statusHistoryExpanded ? 'chevron-up' : 'chevron-down'}
                    size={14}
                    color={AppStyles.colorSet.info}
                  />
                </Pressable>

                {statusHistoryExpanded && (
                  <View style={styles.timeline}>
                    {statusHistory.slice().reverse().map((entry, idx) => (
                      <View key={idx} style={styles.timelineEntry}>
                        <View style={styles.timelineDot} />
                        <View style={styles.timelineContent}>
                          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                            <StatusBadge status={entry.status} compact />
                            <CustomText font="regular" size={11} color={AppStyles.colorSet.textMuted}>
                              {entry.changedBy} · {formatDate(entry.changedAt)}
                            </CustomText>
                          </View>
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Activity Timeline */}
          <ActivityTimeline activityLog={activityLog} />

          {/* Todo List Section */}
          <View style={styles.singleColumnSection}>
            <View style={styles.adminOnlyIndicator}>
              <Feather name="lock" size={10} color={AppStyles.colorSet.textMuted} />
              <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted}>
                {t('admin.adminOnly')}
              </CustomText>
            </View>
            <TodoList
              todos={todos}
              onAddTodo={addTodo}
              onToggleTodo={toggleTodo}
              onDeleteTodo={deleteTodo}
              isAdmin={true}
              containerStyle={{ marginVertical: 0 }}
            />
          </View>

          {/* Attachments Section */}
          <View style={styles.singleColumnSection}>
            <View style={styles.adminOnlyIndicator}>
              <Feather name="lock" size={10} color={AppStyles.colorSet.textMuted} />
              <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted}>
                {t('admin.adminOnly')}
              </CustomText>
            </View>
            <AttachmentList
              attachments={attachments}
              onUpload={uploadAttachment}
              onDownload={downloadAttachment}
              onDelete={deleteAttachment}
              isAdmin={true}
              containerStyle={{ marginVertical: 0 }}
            />
          </View>

          {/* Work Order link - only shown when the org has it configured */}
          <WorkOrderLinkButton organization={organization} />

          {/* Comments Section - at the bottom */}
          <View style={styles.commentsSection}>
            <ReportComments
              comments={comments}
              isAdmin={true}
              isClosed={false}
              showInput={true}
              inputValue={commentInputText}
              onInputChange={setCommentInputText}
              onSubmit={postCommentToEntry}
              submitting={submittingComment}
              alwaysShowInput={true}
              webStyles={webStyles}
              mobileStyles={mobileStyles}
              commentsContainerStyle={isWeb ? webStyles.commentsContainer : mobileStyles.commentsContainer}
              commentContainerStyle={isWeb ? webStyles.commentContainer : mobileStyles.commentContainer}
              isWeb={isWeb}
              adminOnlyValue={adminOnlyComment}
              onAdminOnlyChange={setAdminOnlyComment}
              currentUserEmail={currentUserEmail}
              onEditComment={editComment}
              onDeleteComment={deleteComment}
            />
          </View>

          {/* Report ID Footer - subtle */}
          <View style={styles.reportIdContainer}>
            <CustomText font="regular" size={11} color={AppStyles.colorSet.textMuted}>
              {t('details.reportIdValue', { id: uuid })}
            </CustomText>
          </View>
        </>
      )}

      {/* Confirmation Modal for destructive actions */}
      <ConfirmationModal
        visible={confirmModal.visible}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmColor={confirmModal.confirmColor}
        icon={confirmModal.icon}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, visible: false }))}
      />
    </ReportLayout>
  );
};

export default AdminReportDetails;
